import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Today as TodayIcon,
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import api from '../../services/api';

dayjs.locale('fr');

const Ephemerides = () => {
  const navigate = useNavigate();
  const [todayEphemeris, setTodayEphemeris] = useState(null);
  const [recentEphemerides, setRecentEphemerides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [yearDialogOpen, setYearDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedYear, setSelectedYear] = useState(dayjs().year());

  const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000/api';

  const fetchTodayEphemeris = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/ephemeris/today');
      setTodayEphemeris(response.data.data);
    } catch (err) {
      setError('Erreur lors du chargement de l\'éphéméride du jour');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecentEphemerides = useCallback(async () => {
    try {
      const response = await api.get('/ephemeris', {
        params: { limit: 10, generated: true }
      });
      setRecentEphemerides(response.data.data || []);
    } catch (err) {
      console.error('Erreur chargement éphémérides récentes:', err);
    }
  }, []);

  useEffect(() => {
    fetchTodayEphemeris();
    fetchRecentEphemerides();
  }, [fetchTodayEphemeris, fetchRecentEphemerides]);

  const handleGenerateForDate = async () => {
    try {
      setGenerating(true);
      await api.post('/ephemeris/generate', {
        date: selectedDate.format('YYYY-MM-DD')
      });
      setSuccess('Éphéméride générée avec succès');
      setDialogOpen(false);
      fetchTodayEphemeris();
      fetchRecentEphemerides();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateYear = async () => {
    try {
      setGenerating(true);
      await api.post('/ephemeris/generate-year', { year: selectedYear });
      setSuccess(`Génération des éphémérides pour ${selectedYear} lancée en arrière-plan`);
      setYearDialogOpen(false);
    } catch (err) {
      setError('Erreur lors du lancement de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendToday = async () => {
    if (!todayEphemeris) return;
    
    try {
      setLoading(true);
      await api.post('/whatsapp/send-daily', {
        ephemeride_id: todayEphemeris.id
      });
      setSuccess('Envoi aux abonnés lancé');
    } catch (err) {
      setError('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (ephemeris, type = 'frame') => {
    if (!ephemeris) return null;
    
    // Déterminer le chemin selon le type et la langue
    let imagePath;
    if (type === 'frame') {
      // Préférer la version FR si elle existe, sinon la version par défaut
      imagePath = ephemeris.image_path_with_frame_fr || ephemeris.image_path_with_frame;
    } else {
      imagePath = ephemeris.image_path_simple_fr || ephemeris.image_path_simple;
    }
    
    if (!imagePath) return null;
    
    // Nettoyer le chemin si nécessaire
    const cleanPath = imagePath.replace(/^\/app\/generated\//, '');
    
    return `${API_URL.replace('/api', '')}/generated/${cleanPath}`;
  };

  if (loading && !todayEphemeris) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Éphémérides
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Actions */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Générer pour une date
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CalendarIcon />}
              onClick={() => setYearDialogOpen(true)}
            >
              Générer toute l'année
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              color="success"
              startIcon={<SendIcon />}
              onClick={handleSendToday}
              disabled={!todayEphemeris || loading}
            >
              Envoyer aux abonnés
            </Button>
            <IconButton onClick={() => { fetchTodayEphemeris(); fetchRecentEphemerides(); }}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Paper>

        {/* Éphéméride du jour */}
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TodayIcon /> Éphéméride du jour
        </Typography>

        {todayEphemeris ? (
          <Grid container spacing={3}>
            {/* Image avec cadre */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Version avec cadre
                    <Chip label="Pour WhatsApp" size="small" sx={{ ml: 1 }} />
                  </Typography>
                </CardContent>
                {todayEphemeris.image_path_with_frame ? (
                  <CardMedia
                    component="img"
                    image={getImageUrl(todayEphemeris, 'frame')}
                    alt="Éphéméride avec cadre"
                    sx={{ maxHeight: 500, objectFit: 'contain' }}
                  />
                ) : (
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                    <ImageIcon sx={{ fontSize: 80, color: 'grey.400' }} />
                  </Box>
                )}
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    href={getImageUrl(todayEphemeris, 'frame')}
                    target="_blank"
                    disabled={!todayEphemeris.image_path_with_frame}
                  >
                    Télécharger
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => navigate(`/ephemerides/preview/${todayEphemeris.date}`)}
                  >
                    Aperçu
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Image simple */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Version simple
                    <Chip label="Pour impression" size="small" color="secondary" sx={{ ml: 1 }} />
                  </Typography>
                </CardContent>
                {todayEphemeris.image_path_simple ? (
                  <CardMedia
                    component="img"
                    image={getImageUrl(todayEphemeris, 'simple')}
                    alt="Éphéméride simple"
                    sx={{ maxHeight: 500, objectFit: 'contain' }}
                  />
                ) : (
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                    <ImageIcon sx={{ fontSize: 80, color: 'grey.400' }} />
                  </Box>
                )}
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    href={getImageUrl(todayEphemeris, 'simple')}
                    target="_blank"
                    disabled={!todayEphemeris.image_path_simple}
                  >
                    Télécharger
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Informations du proverbe */}
            {todayEphemeris.proverb && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Proverbe du jour</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">En Duala</Typography>
                        <Typography variant="body1" fontStyle="italic" sx={{ mb: 2 }}>
                          {todayEphemeris.proverb.duala_text}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Traduction</Typography>
                        <Typography variant="body1" color="error.main">
                          {todayEphemeris.proverb.french_text}
                        </Typography>
                      </Grid>
                      {todayEphemeris.proverb.french_meaning && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="textSecondary">Sens</Typography>
                          <Typography variant="body2">
                            {todayEphemeris.proverb.french_meaning}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">
              Aucune éphéméride générée pour aujourd'hui
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => {
                setSelectedDate(dayjs());
                setDialogOpen(true);
              }}
            >
              Générer maintenant
            </Button>
          </Paper>
        )}

        {/* Liste des éphémérides récentes */}
        {recentEphemerides.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Éphémérides récentes
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Jour</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentEphemerides.map((eph) => (
                    <TableRow key={eph.id}>
                      <TableCell>{dayjs(eph.date).format('DD/MM/YYYY')}</TableCell>
                      <TableCell>{dayjs(eph.date).format('dddd')}</TableCell>
                      <TableCell>
                        <Chip
                          label={eph.is_generated ? 'Générée' : 'En attente'}
                          color={eph.is_generated ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/ephemerides/preview/${eph.date}`)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Dialog génération par date */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>Générer une éphéméride</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <DatePicker
                label="Sélectionner une date"
                value={selectedDate}
                onChange={setSelectedDate}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button 
              onClick={handleGenerateForDate} 
              variant="contained"
              disabled={generating}
            >
              {generating ? <CircularProgress size={24} /> : 'Générer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog génération année */}
        <Dialog open={yearDialogOpen} onClose={() => setYearDialogOpen(false)}>
          <DialogTitle>Générer toutes les éphémérides de l'année</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Cette opération génère 365 éphémérides et peut prendre plusieurs minutes.
            </Alert>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Année</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                label="Année"
              >
                {[2024, 2025, 2026, 2027, 2028].map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setYearDialogOpen(false)}>Annuler</Button>
            <Button 
              onClick={handleGenerateYear} 
              variant="contained"
              color="secondary"
              disabled={generating}
            >
              {generating ? <CircularProgress size={24} /> : 'Lancer la génération'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Ephemerides;
